import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  PageHeader, Panel, Button, Field, TextInput, TextArea, Select, Banner, Spinner, useCkToast,
} from '@NP-Webber/crm-kit/dist/next/index.js';
import { AudioRecorder } from '@NP-Webber/crm-kit';
import api from '../../services/api.js';
import { FormGrid, Row, apiError } from '../../next/ui';

/**
 * כרטיס כולל — new design. Same endpoints as KolelCard (/kolels/:id, audio upload / record / remove).
 * Rendered as a page (/kolels/:id) or inside the list's side sheet (recordId + onClose).
 */
const EMPTY_FORM = {
  code: '', name: '', city: '', headFirstName: '', headLastName: '', secondHead: '', schedule: '', subject: '', simanim: '',
  additions: '', gabaiName: '', gabaiPhone: '', totalQuestions: '', memberCount: '', audioFilename: '', status: 'active',
};
const SCHEDULE_OPTIONS = [{ value: '', label: '—' }, ...['לפנה"צ', 'אחה"צ', 'ערב', 'כל היום'].map((s) => ({ value: s, label: s }))];

export default function KolelCardNext({ recordId, onClose }) {
  const toast = useCkToast();
  const params = useParams();
  const navigate = useNavigate();
  const id = recordId || params.id;
  const isNew = id === 'new';
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [audioSrc, setAudioSrc] = useState(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (isNew) { setForm(EMPTY_FORM); setLoading(false); return; }
    setLoading(true);
    api.get(`/kolels/${id}`)
      .then(({ data }) => setForm({ ...EMPTY_FORM, ...data }))
      .catch(() => setError('שגיאה בטעינת נתוני כולל'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  useEffect(() => {
    let url = null;
    if (!isNew && form.audioFilename) {
      api.get(`/kolels/${id}/audio`, { responseType: 'blob' })
        .then(({ data }) => { url = URL.createObjectURL(data); setAudioSrc(url); })
        .catch(() => setAudioSrc(null));
    } else setAudioSrc(null);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [id, isNew, form.audioFilename]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const uploadAudio = async (file, recorded = false) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('audio', file);
    setAudioUploading(true);
    try {
      const { data } = await api.post(`/kolels/${id}/audio`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((f) => ({ ...f, audioFilename: data.audioFilename }));
      if (data.yemotSynced) toast.success(recorded ? 'ההקלטה נשמרה בהצלחה וסונכרנה לקו הטלפון' : 'קובץ השמע הועלה בהצלחה וסונכרן לקו הטלפון');
      else toast(recorded ? 'ההקלטה נשמרה (לא סונכרנה לקו — בדוק הגדרת טוקן ימות)' : 'קובץ השמע הועלה (לא סונכרן לקו — בדוק הגדרת טוקן ימות)', { tone: 'warning' });
    } catch (e) {
      toast.error(apiError(e, recorded ? 'שגיאה בשמירת ההקלטה' : 'שגיאה בהעלאת קובץ שמע'));
    } finally {
      setAudioUploading(false);
    }
  };
  const recordAudio = (blob) => {
    const ext = blob.type.includes('ogg') ? 'ogg' : 'webm';
    uploadAudio(new File([blob], `recording.${ext}`, { type: blob.type }), true);
  };
  const removeAudio = async () => {
    try {
      await api.delete(`/kolels/${id}/audio`);
      setForm((f) => ({ ...f, audioFilename: '' }));
      toast.success('קובץ השמע הוסר');
    } catch (e) {
      toast.error(apiError(e, 'שגיאה בהסרת קובץ שמע'));
    }
  };

  const save = async () => {
    setError('');
    setSaving(true);
    const payload = { ...form, code: form.code !== '' && form.code !== null ? Number(form.code) : null };
    try {
      if (isNew) {
        const { data } = await api.post('/kolels', payload);
        toast.success('כולל נוצר בהצלחה');
        if (onClose) onClose('created'); else navigate(`/kolels/${data._id}`);
      } else {
        const { data } = await api.put(`/kolels/${id}`, payload);
        setForm({ ...EMPTY_FORM, ...data });
        toast.success('נשמר בהצלחה');
        onClose?.('saved', { keepOpen: true });
      }
    } catch (e) {
      setError(apiError(e, 'שגיאת שמירה'));
    } finally {
      setSaving(false);
    }
  };
  const back = () => (onClose ? onClose() : navigate('/kolels'));

  if (loading) return <div className="ck-empty"><Spinner size={26} /></div>;

  return (
    <div className="nx-stack">
      {!recordId ? (
        <button type="button" className="adm-back" onClick={back}><span aria-hidden="true">→</span>כוללים</button>
      ) : null}
      <PageHeader
        title={isNew ? 'כולל חדש' : form.name || 'כולל'}
        sub={isNew ? 'כרטיס כולל' : [form.code ? `קוד ${form.code}` : null, form.city, form.status === 'active' ? 'פעיל' : 'לא פעיל'].filter(Boolean).join(' · ')}
        primary={{ label: saving ? 'שומר…' : isNew ? 'צור כולל' : 'שמור שינויים', icon: 'check', onClick: save }}
      >
        <Button variant="ghost" onClick={back}>{recordId ? 'סגירה' : 'חזרה לרשימה'}</Button>
      </PageHeader>
      {error ? <Banner tone="danger" icon="alert" onClose={() => setError('')}>{error}</Banner> : null}

      <Panel title="פרטי כולל" icon="building">
        <FormGrid cols={3}>
          <Field label="קוד" hint="קוד מספרי ייחודי"><TextInput type="number" ltr value={form.code ?? ''} onChange={set('code')} data-autofocus /></Field>
          <Field label="שם הכולל"><TextInput value={form.name || ''} onChange={set('name')} /></Field>
          <Field label="עיר"><TextInput value={form.city || ''} onChange={set('city')} /></Field>
          <Field label="סדר לימוד"><Select value={form.schedule || ''} onChange={set('schedule')} options={SCHEDULE_OPTIONS} /></Field>
          <Field label="מקצוע"><TextInput value={form.subject || ''} onChange={set('subject')} /></Field>
          <Field label="סטטוס"><Select value={form.status || 'active'} onChange={set('status')} options={[{ value: 'active', label: 'פעיל' }, { value: 'inactive', label: 'לא פעיל' }]} /></Field>
        </FormGrid>
      </Panel>

      <div className="ck-panelgrid" style={{ '--ck-panel-cols': 2 }}>
        <Panel title="ראש כולל" icon="user">
          <FormGrid cols={2}>
            <Field label="שם פרטי"><TextInput value={form.headFirstName || ''} onChange={set('headFirstName')} /></Field>
            <Field label="שם משפחה"><TextInput value={form.headLastName || ''} onChange={set('headLastName')} /></Field>
            <div className="nx-span-all"><Field label="ראש כולל שני"><TextInput value={form.secondHead || ''} onChange={set('secondHead')} /></Field></div>
          </FormGrid>
        </Panel>
        <Panel title="גבאי" icon="phone">
          <FormGrid cols={2}>
            <Field label="שם גבאי"><TextInput value={form.gabaiName || ''} onChange={set('gabaiName')} /></Field>
            <Field label="טלפון גבאי"><TextInput ltr inputMode="tel" value={form.gabaiPhone || ''} onChange={set('gabaiPhone')} /></Field>
          </FormGrid>
        </Panel>
      </div>

      <Panel title="נתונים נוספים" icon="list">
        <FormGrid cols={4}>
          <Field label="מספר אברכים"><TextInput ltr value={form.memberCount || ''} onChange={set('memberCount')} /></Field>
          <Field label='סה"כ שאלות'><TextInput ltr value={form.totalQuestions || ''} onChange={set('totalQuestions')} /></Field>
          <div className="nx-span-2"><Field label="סימנים"><TextInput value={form.simanim || ''} onChange={set('simanim')} /></Field></div>
          <div className="nx-span-all"><Field label="הוספות ותיקונים"><TextArea rows={2} value={form.additions || ''} onChange={set('additions')} /></Field></div>
        </FormGrid>
      </Panel>

      <Panel title="IVR — ימות המשיח" sub="קובץ שמע להכרזת שם הכולל בתפריט. אם לא הועלה קובץ, המערכת תקרא את שם הכולל באופן אוטומטי." icon="mic" tone="violet">
        {isNew ? <span className="ck-muted">ניתן להעלות קובץ שמע לאחר יצירת הכולל.</span> : (
          <div className="nx-stack">
            <input ref={fileRef} type="file" accept="audio/*" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; uploadAudio(f); }} />
            {form.audioFilename ? (
              <div className="nx-item">
                <div className="nx-item__main" style={{ flexBasis: '100%' }}>
                  <b>{form.audioFilename}</b>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  {audioSrc ? <audio controls src={audioSrc} style={{ width: '100%', maxWidth: 420, marginTop: 6 }} /> : null}
                </div>
              </div>
            ) : null}
            <Row>
              <Button icon="upload" disabled={audioUploading} onClick={() => fileRef.current?.click()}>
                {audioUploading ? 'מעלה…' : form.audioFilename ? 'החלפת קובץ' : 'העלאת קובץ שמע'}
              </Button>
              {form.audioFilename ? <Button variant="ghost" icon="trash" disabled={audioUploading} onClick={removeAudio}>הסרת קובץ</Button> : null}
              <small className="ck-muted">mp3, wav, ogg, m4a — מקסימום 20MB</small>
            </Row>
            <AudioRecorder onSave={recordAudio} saving={audioUploading} disabled={audioUploading} />
          </div>
        )}
      </Panel>
    </div>
  );
}
